/** Bringing a workspace into existence — **B-23's buildable half**.
 *
 * Until these existed a customer workspace could not be created in a production deployment at all:
 * every company, site, meter and period came from `seed.py`, which refuses to run in production.
 *
 * **Nothing here sends an `org_id`.** The session decides which organisation this is, and the
 * server takes it from the principal — a tenant id in a request body is a tenant id a caller can
 * change. The same rule `useFacilities` follows, for the same reason.
 *
 * Every mutation invalidates the reads it affects, because the screen that submitted the form is
 * the screen showing the list it just changed; a stale list after a create is the one moment
 * somebody presses the button twice.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom, Refused } from "./refusal";
import type { components } from "./schema";

export type Entity = components["schemas"]["EntityOut"];
export type Facility = components["schemas"]["FacilityOut"];
export type Meter = components["schemas"]["MeterOut"];
export type ReportingPeriod = components["schemas"]["ReportingPeriodOut"];

export type EntityIn = components["schemas"]["LegalEntityIn"];
export type FacilityIn = components["schemas"]["FacilityIn"];
export type MeterIn = components["schemas"]["MeterIn"];
export type ReportingPeriodIn = components["schemas"]["ReportingPeriodIn"];

/** The server's sentence, not ours.
 *
 * Every refusal on this surface is written to be read by the person who hit it — *"Sierravale
 * Foods Ltd is already the parent of this group"*, not *"conflict"*. Replacing it with a generic
 * message here would throw away the only part of the response that tells them what to do.
 */
function refusal(error: unknown): string {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string") return detail;

  // The product's own refusals: `{error, message}`. Narrowed rather than cast, because a cast
  // would still compile on the day the shape changes and the caller would be shown "undefined".
  if (detail && typeof detail === "object" && !Array.isArray(detail) && "message" in detail) {
    const { message } = detail as Record<"message", unknown>;
    if (typeof message === "string") return message;
  }

  // FastAPI's own validation shape. A missing `boundary_approach` arrives like this, and the
  // field name is the useful half — `loc` is a path, so the last element is the field.
  // `Array.isArray` on an `unknown` narrows to `any[]`, which defeats the point — so the array is
  // re-typed as `unknown[]` and every value out of it is narrowed by hand.
  const problems: readonly unknown[] = Array.isArray(detail) ? (detail as unknown[]) : [];
  if (problems.length > 0) {
    const first = (problems[0] ?? {}) as { loc?: unknown; msg?: unknown };
    const path: unknown[] = Array.isArray(first.loc) ? first.loc : [];
    const field: unknown = path.at(-1);
    const said = typeof first.msg === "string" ? first.msg : "is required";
    // Only a string or a number names a field. Anything else stringifies to "[object Object]",
    // which would put a worse message in front of somebody than no field name at all.
    return typeof field === "string" || typeof field === "number"
      ? `${field}: ${said}`
      : said;
  }

  return "That could not be saved.";
}

/** The status and the code alongside the sentence `refusal` already built.
 *
 * `refusal` handles the one shape `refusalFrom` cannot: FastAPI's own 422 body is a **list** of
 * validation errors, and naming the field is more use to somebody filling in a form than any
 * sentence this file could write. So the structured refusal wins where the route wrote one, and
 * that list reading is the fallback — rather than one replacing the other.
 */
export function provisioningRefusal(response: Response, error: unknown): Refused {
  const structured = refusalFrom(response, error, "");
  if (structured.message) return structured;
  return new Refused(response.status, structured.code, refusal(error));
}


export function useCreateEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: EntityIn): Promise<Entity> => {
      const { data, error, response } = await api.POST("/api/v1/entities", { body: input });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["entities"] });
    },
  });
}

export function useCreateFacility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: FacilityIn): Promise<Facility> => {
      const { data, error, response } = await api.POST("/api/v1/facilities", { body: input });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: () => {
      // Both: a new site changes the facility list **and** the entity's `facility_count`, which is
      // the figure that tells somebody an entity contributes nothing.
      void queryClient.invalidateQueries({ queryKey: ["facilities"] });
      void queryClient.invalidateQueries({ queryKey: ["entities"] });
    },
  });
}

export function useRetireFacility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      facilityId: string;
      effective_from: string;
      /** An Admin's reason, sent only on the second attempt after `period_closed` (ADR-0094). */
      postCloseReason?: string;
    }): Promise<Facility> => {
      const { data, error, response } = await api.POST("/api/v1/facilities/{facility_id}/retire", {
        params: { path: { facility_id: input.facilityId } },
        body: {
          effective_from: input.effective_from,
          ...(input.postCloseReason ? { post_close_reason: input.postCloseReason } : {}),
        },
      });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["facilities"] });
      void queryClient.invalidateQueries({ queryKey: ["meters"] });
      // **And the census, which is what a closure changes about the figures** (CTX-03). A site's
      // `operational_to` is one of the three dates `census.life_of` intersects, so every stream at
      // the site stops being judged the moment this returns — and a census pane left on screen
      // would go on reporting the months afterwards as missing. The key is the prefix rather than
      // one window, because this screen does not know which window anybody is looking at and a
      // source's links hang off the same key.
      void queryClient.invalidateQueries({ queryKey: ["sources"] });
    },
  });
}

/** A supply point is removed — **CTX-03's acceptance clause, from the screen**.
 *
 * The route has existed and nothing called it, which is `useIssueArtifact`'s shape: a built,
 * tested, documented write that no control asks for. Retiring the whole site was the only way to
 * record one meter coming out, and that retires every supply at it.
 *
 * **Three caches, because a retirement changes three answers.** The site's meter list (it now
 * carries an end date), the census (`census.life_of` intersects the meter's dates with the
 * declaration's and the site's, so months after the removal stop being judged), and the per-source
 * links that hang off the census key. `facilities` is deliberately **not** invalidated: nothing
 * about the site changed, and a meter's removal is not a site's closure.
 *
 * `facilityId` is passed rather than read from the response so the invalidation names the same key
 * `useMeters` used — `["meters", facilityId]`. The response carries `facility_id` too, and using it
 * would be a second answer to which list went stale.
 */
export function useRetireMeter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      meterId: string;
      facilityId: string;
      effective_from: string;
      /** An Admin's reason, sent only on the second attempt after `period_closed` (ADR-0094). */
      postCloseReason?: string;
    }): Promise<Meter> => {
      const { data, error, response } = await api.POST("/api/v1/meters/{meter_id}/retire", {
        params: { path: { meter_id: input.meterId } },
        body: {
          effective_from: input.effective_from,
          ...(input.postCloseReason ? { post_close_reason: input.postCloseReason } : {}),
        },
      });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: (_meter, input) => {
      void queryClient.invalidateQueries({ queryKey: ["meters", input.facilityId] });
      void queryClient.invalidateQueries({ queryKey: ["sources"] });
    },
  });
}

export function useMeters(facilityId: string | null) {
  return useQuery({
    queryKey: ["meters", facilityId] as const,
    enabled: facilityId !== null,
    queryFn: async (): Promise<Meter[]> => {
      const { data, error, response } = await api.GET("/api/v1/meters", {
        params: { query: { facility_id: facilityId as string } },
      });
      if (error) throw refusalFrom(response, error, "This site's meters could not be read.");
      return data ?? [];
    },
  });
}

export function useCreateMeter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: MeterIn): Promise<Meter> => {
      const { data, error, response } = await api.POST("/api/v1/meters", { body: input });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: (_meter, input) => {
      void queryClient.invalidateQueries({ queryKey: ["meters", input.facility_id] });
    },
  });
}

export function useCreatePeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReportingPeriodIn): Promise<ReportingPeriod> => {
      const { data, error, response } = await api.POST("/api/v1/periods", { body: input });
      if (error || !data) throw provisioningRefusal(response, error);
      return data;
    },
    onSuccess: () => {
      // The period selector in the shell reads the organisation, so that is what goes stale.
      void queryClient.invalidateQueries({ queryKey: ["organization"] });
    },
  });
}
